/**
 * Shared silent JWT refresh — single in-flight refresh for all axios callers.
 * Access tokens expire (~15m–1h); refresh tokens last days. Without this,
 * raw axios pages kick users to sign-in after a short idle period.
 */
import axios from 'axios';
import { serverURL } from '../../constants';
import { applySessionClaims, clearSessionAuth } from './session';

const REFRESH_PATH = '/api/auth/refresh';
/** Refresh when access token has < this many seconds left */
const EXPIRY_SKEW_SEC = 90;

let refreshPromise = null;

function decodeJwtPayload(token) {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(b64);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function getAccessTokenExpiryMs(token = sessionStorage.getItem('token')) {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return null;
    return payload.exp * 1000;
}

export function isAccessTokenExpiringSoon(token = sessionStorage.getItem('token'), skewSec = EXPIRY_SKEW_SEC) {
    const expMs = getAccessTokenExpiryMs(token);
    if (expMs == null) return !token;
    return Date.now() >= expMs - skewSec * 1000;
}

export function isAccessTokenExpired(token = sessionStorage.getItem('token')) {
    const expMs = getAccessTokenExpiryMs(token);
    if (expMs == null) return !token;
    return Date.now() >= expMs;
}

/**
 * Exchange refresh token for a new access (+ refresh) pair.
 * Concurrent callers share one in-flight request.
 * @returns {Promise<string|null>} new access token, or null if refresh failed
 */
export async function refreshAccessToken() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = sessionStorage.getItem('refreshToken');
        if (!refreshToken) return null;

        try {
            const { data } = await axios.post(
                `${serverURL}${REFRESH_PATH}`,
                { refreshToken },
                {
                    timeout: 15000,
                    // Prevent auth/transient interceptors from retrying the refresh itself
                    skipAuthRefresh: true,
                    skipTransientRetry: true,
                }
            );
            const access = data?.accessToken || data?.token;
            if (!access) return null;

            sessionStorage.setItem('token', access);
            if (data.refreshToken) {
                sessionStorage.setItem('refreshToken', data.refreshToken);
            }
            applySessionClaims({
                organizationId: data.organizationId,
                orgRole: data.orgRole,
                platformAdmin: data.platformAdmin,
            });
            return access;
        } catch {
            return null;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

/**
 * Ensure a usable access token exists (refresh if missing/expired/expiring).
 * @returns {Promise<string|null>}
 */
export async function ensureFreshAccessToken({ force = false } = {}) {
    const token = sessionStorage.getItem('token');
    const refresh = sessionStorage.getItem('refreshToken');
    if (!token && !refresh) return null;
    if (!force && token && !isAccessTokenExpiringSoon(token)) {
        return token;
    }
    if (!refresh) return token || null;
    return refreshAccessToken();
}

export function forceSignOutToSignIn() {
    clearSessionAuth();
    try {
        localStorage.removeItem('user');
    } catch { /* ignore */ }
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path && path !== '/signin' && path !== '/' && path !== '/signup') {
        window.location.href = `/signin?from=${encodeURIComponent(path)}`;
    }
}

export function isAuthRefreshUrl(url = '') {
    return String(url).includes(REFRESH_PATH)
        || String(url).includes('/api/signin')
        || String(url).includes('/api/signup');
}

export default refreshAccessToken;
